import noop from './noop.js';

/**
 * @typedef {import('vue').VueConstructor} Vue
 */

/**
 * @callback RedirectFunction
 * @param {number} status
 * @param {string} url
 * @returns {void}
 */

/**
 * @typedef {import('vuex').Store} Vuex
 */

/**
 * @typedef {import('vue-router').Route} VueRoute
 */

/**
 * @typedef {import('vue-router').default} VueRouter
 */

/**
 * @typedef {import('express').Request} ExpressRequest
 */

/**
 * @typedef {import('express').Response} ExpressResponse
 */

/**
 * @typedef {object} ThemeMiddlewareContext
 * @property {Vue} app Instance of Vue application with attached custom plugins
 * @property {object} env Object with application global configuration
 * @property {boolean} isClientProcess Boolean if current execution context is client side
 * @property {boolean} isServerProcess Boolean if current execution context is server side
 * @property {RedirectFunction} redirect Function to tell the application to redirect and break the middlewares sequence
 * @property {VueRoute} route Object of Vue route to be loaded
 * @property {VueRouter} router Instance of Vue router
 * @property {Vuex} store Instance of Vuex store
 * @property {ExpressRequest} [request] Express request object (available only on server)
 * @property {ExpressResponse} [response] Express response object (available only on server)
 */

/**
 * @callback ThemeMiddlewareHandler
 * @param {ThemeMiddlewareContext} context
 */

/**
 * @callback ThemeMiddlewarePredicate
 * @param {ThemeMiddlewareContext} context
 */

/**
 * Utility to define shared middleware for theme pages consumed by entry file
 * @param {ThemeMiddlewareHandler} handler Callback method used as a middleware
 * @param {ThemeMiddlewarePredicate} predicate Predicate of the middleware to be called
 * @returns {Function} Middleware function to be handled in entry file
 */
export default function defineThemeMiddleware(handler, predicate) {
	return async (context) => {
		const fineToExecute = typeof predicate === 'function' ? await predicate(context) : true;
		return fineToExecute ? handler(context) : noop();
	};
}
